/**
 * templateLinkApi.js — thin wrappers over Api_Functions for the workflow <-> template links (story 224).
 * One function per Sun endpoint (contract rows E1-E3,`03-dominio.md` §"Contrato cross-repo congelado" as amended by
 * `contrato-sun.md`). No business logic lives here: this is the only file that spells the `workflow/{id}/templateLinks*` routes.
 */
import { apiGet, apiPost } from '../../../generic/Api_Functions'

/** E1 — `GET workflow/{workflowId}/templateLinks` -> WorkflowTemplateLinks `{workflowId, latestVersion, links[]}` */
export const getTemplateLinks = (workflowId) => apiGet('workflow/' + workflowId + '/templateLinks')

/**
 * E2 — `POST workflow/{workflowId}/templateLinks/dismiss` `{subworkflowNodeId|null, templateVersion}` -> TemplateLinkView.
 * Needs the permission to save workflows (H4); errors are Sun's literals (contrato-sun.md §9.4).
 */
export const dismissTemplateLink = (workflowId, body) => apiPost('workflow/' + workflowId + '/templateLinks/dismiss', body)

/**
 * E3 — `GET workflow/{workflowId}/templateLinks/updateImpact?subworkflowNodeId=<uuid>&targetTemplateWorkflowId=<id>` -> TemplateUpdateImpact.
 * `subworkflowNodeId` absent (`''`/`null`) means the ROOT unit. Needs the permission to save workflows; read-only, writes nothing.
 */
export const getTemplateUpdateImpact = (workflowId, subworkflowNodeId, targetTemplateWorkflowId) => {
  const query = new URLSearchParams()
  if (subworkflowNodeId) {
    query.set('subworkflowNodeId', subworkflowNodeId)
  }
  query.set('targetTemplateWorkflowId', targetTemplateWorkflowId)
  return apiGet('workflow/' + workflowId + '/templateLinks/updateImpact?' + query.toString())
}

/**
 * The `templateUpdates` of the workflow save body (`PUT workflow/{createNewVersion}/{propagate}/{indexValueName}[/{partReferenceId}]`):
 * one `{subworkflowNodeId, targetTemplateWorkflowId}` per pending intent, `subworkflowNodeId: null` for the ROOT. Sun applies them
 * in the same transaction as the save, for a new version and for an in-place save alike (adjustment 2026-10-29: Jupiter no longer
 * calls E4, the save replaces it). The editor's `''` for the ROOT becomes the contract's `null` here, at the API boundary.
 * @param {object[]} intents the editor's pending intents (`templateLinks.js` `templateUpdateIntent`)
 * @returns {{subworkflowNodeId: (string|null), targetTemplateWorkflowId: number}[]}
 */
export const toTemplateUpdatesBody = (intents = []) => intents.map((intent) => ({
  subworkflowNodeId: intent.scope === 'ROOT' || !intent.subworkflowNodeId ? null : intent.subworkflowNodeId,
  targetTemplateWorkflowId: intent.targetTemplateWorkflowId
}))

/**
 * DL25-a (Y8): the target template version of an accepted update, loaded exactly as inserting it would load it
 * (`GET workflow/{id}`, the same endpoint the editor already uses; there is no new one). Accepting replaces the content of the unit with it.
 */
export const getTemplateTargetWorkflow = (workflowId) => apiGet('workflow/' + workflowId)

/**
 * The `templateLinkChanges` of the workflow save body (HU-05, contract `TemplateLinkChangeEntry`): one entry per pending decision, with
 * exactly the four fields of the contract. The editor's `''` for the ROOT becomes `null` here, at the API boundary; `indexValueName` is
 * only sent with Publish (and `null` when the index setting is off); `bomAndDocumentsFromEditor` is only ever true on the ROOT (DL26).
 * @param {object[]} decisions the editor's pending decisions (`templateLinks.js` `templateDecision`)
 * @param {{rootBomAndDocumentsFromEditor: boolean}} options
 */
export const toTemplateLinkChangesBody = (decisions = [], { rootBomAndDocumentsFromEditor = false } = {}) => decisions.map((decision) => ({
  subworkflowNodeId: decision.scope === 'ROOT' || !decision.subworkflowNodeId ? null : decision.subworkflowNodeId,
  action: decision.action,
  indexValueName: decision.action === 'PUBLISH' && decision.indexValueName ? decision.indexValueName : null,
  bomAndDocumentsFromEditor: decision.scope === 'ROOT' && rootBomAndDocumentsFromEditor
}))
